import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { NotificationLinkItem, NotificationProps } from "@/components/ui/notification";
import { ApiError, api } from "@/lib/api";
import { useKeys } from "@/lib/queries";
import { relativeTime } from "@/lib/utils";

/**
 * A person's notifications (ACL-506, ADR-161 and ADR-162 in platform): what the console tells
 * somebody without being asked. An appointment's and a callback's reminders, fifteen and five
 * minutes before; a callback the dialer gave to another rep; anything else core-api is given to
 * say (`notifications.notify` there).
 *
 * Every kind comes in one shape, already in words: a title, a message, a few lines of detail, the
 * moment it is about, whether that still stands, where it opens and what else is one link away.
 * So a new kind needs nothing here: `notificationView` turns any of them into what `Notification`
 * (`components/ui/notification.tsx`) draws, and the bell, the toasts and the tab's title read it.
 *
 * Read from `GET /notifications`, the person's own, newest first, with how many they have not
 * seen; marked seen with `POST /notifications/read`. A new one or a read sends a hint on the
 * person's own channel of `GET /events` (`notifications`, `lib/acd.ts`), so the bell moves within
 * a second; the minute's poll is for a hint that is lost. The rules here are pure, with tests.
 */

/** Where a notification opens, named by what it is; this console knows its own paths. */
export interface NotificationTarget {
  kind: "lead" | "conversation" | "appointment" | "path";
  /** The lead's or the appointment's id, or for `path` the console path itself. */
  id: string;
}

export interface NotificationLink {
  label: string;
  target: NotificationTarget;
}

export interface UserNotification {
  id: number;
  /** appointment_reminder | callback_reminder | callback_handover | another writer's own. */
  kind: string;
  created_at: string;
  /** Null until the person has seen it. */
  read_at: string | null;
  title: string;
  message: string | null;
  details: string[];
  tone: "info" | "warning";
  /** The moment it is about: an appointment's start, a callback's time. */
  at: string | null;
  /** What it is about, in a word, when that no longer stands: "Cancelled", "Done". */
  status: string | null;
  target: NotificationTarget | null;
  links: NotificationLink[];
}

export interface NotificationList {
  items: UserNotification[];
  unread: number;
}

/** The bell's poll, for a hint that never came. */
const POLL_MS = 60_000;

/** A 404: the core-api behind the console has no notifications yet. */
export function notThere(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

/** The person's notifications, for the bell, its list, the toasts and the tab's title: one query. */
export function useNotifications() {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.scoped(["notifications"]),
    queryFn: () => api.get<NotificationList>("/notifications"),
    // A core-api with no notifications is asked once, not every minute.
    retry: (count, error) => !notThere(error) && count < 3,
    refetchInterval: (query) => (notThere(query.state.error) ? false : POLL_MS),
  });
}

/** Mark notifications seen: the ones named, or every one. The bell is read again after. */
export function useReadNotifications() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: readonly number[] | null) =>
      api.post<{ unread: number }>("/notifications/read", ids ? { ids } : {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.scoped(["notifications"]) }),
  });
}

/** The console path a target opens; null for one it does not know, or a path that leaves it. */
export function targetPath(target: NotificationTarget | null | undefined): string | null {
  if (!target) return null;
  switch (target.kind) {
    case "lead":
      return `/contacts/${target.id}`;
    case "conversation":
      return `/conversations/team?lead=${target.id}`;
    case "appointment":
      return `/calendars?tab=list&appointment=${target.id}`;
    case "path":
      // The console's own pages only: never somewhere else, never a protocol-relative URL.
      return target.id.startsWith("/") && !target.id.startsWith("//") ? target.id : null;
    default:
      return null;
  }
}

/**
 * How far away the moment is, as a line under the message: "In 4 min", "In 1 h 20 min", "Now",
 * "4 min ago". Past the hour after it, nothing: the message says when it was.
 */
export function countdown(atIso: string | null, now: number): string | null {
  if (!atIso) return null;
  const minutes = (Date.parse(atIso) - now) / 60_000;
  if (Number.isNaN(minutes)) return null;
  if (minutes >= 60) {
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes - h * 60);
    return m ? `In ${h} h ${m} min` : `In ${h} h`;
  }
  if (minutes >= 1) return `In ${Math.round(minutes)} min`;
  if (minutes > -1) return "Now";
  if (minutes > -60) return `${Math.round(-minutes)} min ago`;
  return null;
}

/** A mention in a chat (platform ADR-186): its message is the chat's words, read in the chat. */
export const KIND_CHAT_MENTION = "chat_mention";

/**
 * Any notification as `Notification` draws it, links in this console's paths. A mention says who
 * mentioned the person and where, not what they wrote (ACL-594): pressed, it opens the chat.
 */
export function notificationView(n: UserNotification, now: number): NotificationProps {
  const links: NotificationLinkItem[] = [];
  for (const link of n.links) {
    const href = targetPath(link.target);
    if (href) links.push({ label: link.label, href });
  }
  return {
    id: n.id,
    title: n.title,
    message: n.kind === KIND_CHAT_MENTION ? null : n.message,
    details: n.details,
    time: relativeTime(n.created_at),
    countdown: n.status ? null : countdown(n.at, now),
    status: n.status,
    tone: n.tone,
    unread: n.read_at === null,
    href: targetPath(n.target),
    links,
  };
}

/** The bell's count: "9+" past nine, nothing at none. */
export function bellBadge(unread: number): string | null {
  if (unread <= 0) return null;
  return unread > 9 ? "9+" : String(unread);
}

/**
 * The notifications to toast: unread, about something that still stands, and not among the ones
 * this tab already knew. The first list a tab reads is what it knew, so a page loaded with some
 * waiting toasts none of them; the bell has them.
 */
export function freshNotifications(
  items: readonly UserNotification[],
  known: ReadonlySet<number>,
): UserNotification[] {
  return items.filter((n) => n.read_at === null && n.status === null && !known.has(n.id));
}

/** A count in front of the tab's title, "(2) AI Case Leads — Console"; the title alone at none. */
export function titleWithCount(title: string, unread: number): string {
  const bare = title.replace(/^\(\d+\+?\) /, "");
  if (unread <= 0) return bare;
  return `(${unread > 99 ? "99+" : unread}) ${bare}`;
}
